import { Box, Paper, Typography } from "@mui/material";
import { colors } from "../../../../../../../ui/colors";
import { CapBadge } from "./CapabalityBadge";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PhoneIcon from "@mui/icons-material/Phone";
import MessageOutlinedIcon from "@mui/icons-material/MessageOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { PrimaryButton } from "@components/Buttons/PrimaryButton";
import { getPhoneNumberLocationLabel } from "../helper/get-location-label.helper";

export const NumberCard: React.FC<{
    number: PhoneNumber;
    onSelect: (n: PhoneNumber) => void;
    selected: boolean;
}> = ({ number, onSelect, selected }) => {
    const isRecommended = false;

    const location = getPhoneNumberLocationLabel(number);

    return (
        <Paper
            elevation={0}
            onClick={() => onSelect(number)}
            sx={{
                border: `1.5px solid ${isRecommended
                    ? colors.orange[400]
                    : selected
                        ? colors.orange[300]
                        : colors.gray[100]
                    }`,
                borderRadius: "14px",
                p: "16px 18px",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",

                background: isRecommended
                    ? `linear-gradient(135deg, ${colors.orange[50]} 0%, ${colors.yellow[50]} 100%)`
                    : selected
                        ? colors.orange[50]
                        : "#fff",

                boxShadow: isRecommended
                    ? `0 0 0 2px ${colors.orange[400]}, 0 8px 32px ${colors.orange[500]}38`
                    : selected
                        ? `0 0 0 1px ${colors.orange[200]}, 0 4px 14px ${colors.orange[300]}33`
                        : "0 1px 6px rgba(0,0,0,0.05)",

                transition:
                    "border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease, transform 0.2s ease",

                "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: isRecommended
                        ? `0 0 0 2px ${colors.orange[400]}, 0 8px 32px ${colors.orange[500]}38`
                        : selected
                            ? `0 0 0 1px ${colors.orange[300]}, 0 6px 18px ${colors.orange[300]}38`
                            : "0 4px 14px rgba(0,0,0,0.08)",
                },
            }}
        >
            {isRecommended && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 3,
                        background: `linear-gradient(90deg, ${colors.orange[500]}, ${colors.yellow[500]})`,
                    }}
                />
            )}

            {isRecommended && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        background: `linear-gradient(135deg, ${colors.orange[500]}, ${colors.yellow[500]})`,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "0 12px 0 12px",
                        fontSize: 10,
                        fontWeight: 800,
                        color: "#fff",
                        letterSpacing: "0.06em",
                    }}
                >
                    ★ RECOMMENDED
                </Box>
            )}

            {selected && !isRecommended && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        width: 8,
                        height: 8,
                        borderRadius: "999px",
                        bgcolor: colors.orange[400],
                        boxShadow: `0 0 0 3px ${colors.orange[100]}`,
                    }}
                />
            )}

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: 20,
                            fontWeight: 800,
                            color: selected ? colors.orange[500] : colors.gray[500],
                            letterSpacing: "-0.02em",
                            mb: 0.4,
                        }}
                    >
                        {number.friendlyName}
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mb: 1,
                        }}
                    >
                        <PlaceOutlinedIcon
                            sx={{
                                fontSize: 13,
                                color: selected ? colors.orange[400] : colors.gray[300],
                            }}
                        />

                        <Typography
                            sx={{
                                fontSize: 12,
                                color: colors.gray[400],
                                fontWeight: 500,
                            }}
                        >

                            {location}
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                        <CapBadge
                            label="SMS"
                            icon={<MessageOutlinedIcon />}
                            active={number.capabilities.sms}
                        />

                        <CapBadge
                            label="MMS"
                            icon={<ImageOutlinedIcon />}
                            active={number.capabilities.mms}
                        />

                        <CapBadge
                            label="Voice"
                            icon={<PhoneIcon />}
                            active={number.capabilities.voice}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 1,
                    }}
                >
                    <Box
                        sx={{
                            textAlign: "right",
                            maxWidth: 125,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontWeight: 800,
                                color: selected ? colors.orange[500] : colors.gray[400],
                                lineHeight: 1.3,
                            }}
                        >
                            Flownect pricing
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: 11,
                                color: colors.gray[300],
                                fontWeight: 500,
                                mt: 0.35,
                                lineHeight: 1.35,
                            }}
                        >
                            Shown after selection
                        </Typography>
                    </Box>

                    <PrimaryButton
                        onClick={(event) => {
                            event.stopPropagation();
                            onSelect(number);
                        }}
                        variant={isRecommended ? "contained" : "outlined"}
                        fullWidth={false}
                        sx={{
                            borderRadius: "10px",
                            px: 2.5,
                            py: 0.9,
                            fontSize: 13,
                            fontWeight: 700,
                        }}
                    >
                        {selected ? "Selected" : "Select"}
                    </PrimaryButton>
                </Box>
            </Box>
        </Paper>
    );
};